import { useEffect, useState } from 'react';
import { string } from 'prop-types';
import _ from 'lodash';
import {
    Language as LanguageIcon,
    Settings as SettingsIcon,
    Room as RoomIcon,
    Cloud as CloudIcon,
    Schedule as ScheduleIcon,
} from '@material-ui/icons';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { makeStyles, useTheme } from '@material-ui/core';

import { Plm, ColoredPlmStatus } from '../../icon/Icons';
import { useSetLastLocationState } from '../SidebarLocationHistory';
import { useFilterState } from '../../../stores/FilterStateStore';
import { buildUrlParamValue } from '../../../utilities/FilterStateUrlUtilities';
import { usePlms, DOWN, TIMEOUT, UNKNOWN } from '../../../stores/PlmStateStore';
import { useIsAdminUser } from '../../../stores/SessionStore';
import { MenuGroup, MenuGroupItem } from '../menu';
import { timeRangeFilterId } from '../../filters';
import { businessServicesPageId, sitesOverviewPageId, sitesPageId, reportsPageId } from '../../pages';

const filters = [timeRangeFilterId];

const useStyles = makeStyles(() => ({
    [DOWN]: {
        cursor: 'default !important',
        userSelect: 'none',
    },
}));

export function PlmIcon(props) {
    const { state } = props;

    const theme = useTheme();

    switch (state) {
        case DOWN:
            return <ColoredPlmStatus accentColor={theme.palette.error.main} />;
        case TIMEOUT:
            return <ColoredPlmStatus accentColor={theme.palette.warning.main} />;
        default:
            return <Plm />;
    }
}
PlmIcon.propTypes = { state: string };
PlmIcon.defaultProps = { state: UNKNOWN };

function getPlmHoverText(plm) {
    let statusText;
    switch (plm.state) {
        case DOWN:
            statusText = t`Local Manager is currently unreachable`;
            break;
        case TIMEOUT:
            statusText = t`Local Manager is experiencing significant delays`;
            break;
        default:
            statusText = t`Local Manager is operational`;
            break;
    }

    statusText = i18n._(statusText);
    // If you use the lingui macros or internationalization, it escapes newlines and special newline characters
    // Se we need to make sure to insert any linebreaks for multi line title text outside of the i18n context
    return plm.description ? `${statusText} \n\n ${plm.description}` : statusText;
}

export default function PgmMainMenu() {
    const [filterState] = useFilterState();
    const [activeLink, setActiveLink] = useState(undefined);
    const [isAdminUser] = useIsAdminUser();
    const setLastLocationState = useSetLastLocationState();
    const classes = useStyles();

    const [plms] = usePlms();

    useEffect(() => {
        // Save the active link state in session storage.  This is necessary so that the
        // admin menu back button can return to the correct page.
        if (activeLink) {
            const encodedFilterState = {
                ...(filterState?.encoded || {}),
                ...(activeLink?.encodedFilterState || {}),
            };

            // Our sidebar links now start with a leading slash to avoid relative navigation issues.
            // The page name in portal/ui cannot have a leading slash. We need to drop this
            // in the compatibility layer for portal/ui.
            let page = activeLink.target;
            if (page && page.startsWith('/')) {
                page = page.substr(1);
            }

            setLastLocationState({
                name: 'app.auth.page',
                params: {
                    page,
                    filterState: buildUrlParamValue(_.pick(encodedFilterState, activeLink.filters)),
                },
            });
        }
    }, [activeLink, filterState, setLastLocationState]);

    function handleItemActiveChange(itemLink, active) {
        // Save the active link state in session storage.  This is necessary so that the
        // admin menu back button can return to the correct page.
        if (active) setActiveLink(itemLink);
    }

    return (
        <>
            <MenuGroup id="enterprise" title={t`Global`} expanded>
                <MenuGroupItem
                    id={sitesOverviewPageId}
                    title={t`Sites Overview`}
                    icon={<LanguageIcon />}
                    link={{
                        target: sitesOverviewPageId,
                        filters,
                    }}
                    onActiveChange={handleItemActiveChange}
                />
                <MenuGroupItem
                    id={sitesPageId}
                    title={t`Sites`}
                    icon={<RoomIcon />}
                    link={{
                        target: sitesPageId,
                        filters,
                    }}
                    onActiveChange={handleItemActiveChange}
                />
                <MenuGroupItem
                    id={businessServicesPageId}
                    title={t`Business Services`}
                    icon={<CloudIcon />}
                    link={{
                        target: businessServicesPageId,
                        filters,
                    }}
                    onActiveChange={handleItemActiveChange}
                />
                <MenuGroupItem
                    id="schedule-reports"
                    title={t`Scheduled Reports`}
                    icon={<ScheduleIcon />}
                    link={{
                        target: reportsPageId,
                    }}
                    onActiveChange={handleItemActiveChange}
                />
                {isAdminUser && (
                    <MenuGroupItem
                        id="administration"
                        title={t`Administration`}
                        icon={<SettingsIcon />}
                        link={{
                            target: '/admin',
                        }}
                    />
                )}
            </MenuGroup>
            <MenuGroup id="plms" title={t`Local`} expanded>
                {!!plms &&
                    plms.length > 0 &&
                    plms.map((plm) => (
                        <MenuGroupItem
                            key={plm.id}
                            id={plm.id}
                            title={plm.name}
                            disabled={plm.state === DOWN}
                            className={classes[plm.state]}
                            hoverText={getPlmHoverText(plm)}
                            icon={<PlmIcon state={plm.state} />}
                            link={{
                                plmId: plm.id,
                                target: '/',
                                filters,
                            }}
                            onActiveChange={handleItemActiveChange}
                        />
                    ))}
            </MenuGroup>
        </>
    );
}
